/**
 * Exports station share data to an Excel file (.xlsx) using SheetJS (xlsx).
 * Produces one sheet: State | Market | Shares | Ownership
 *
 * Shares cell format: "CALL-NN% CALL-NN% (buy-rule note)"
 *
 * Ownership is derived by joining StationShareEntry.call_sign →
 * bpl_stations.station and collecting distinct ownership group values.
 * Stations with no BPL match are silently skipped.
 */
export async function exportStationShareToExcel(markets, entriesMap, notesMap, bplStations = []) {
  const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.2/package/xlsx.mjs')

  // Build ownership lookup: call_sign (upper) → ownership
  const ownershipMap = {}
  for (const s of bplStations) {
    if (s.station && s.ownership) {
      ownershipMap[s.station.trim().toUpperCase()] = s.ownership
    }
  }

  // Sort markets by state then market name
  const sorted = [...markets].sort((a, b) => {
    if (a.state < b.state) return -1
    if (a.state > b.state) return 1
    return (a.market || '').localeCompare(b.market || '')
  })

  const rows = [['State', 'Market', 'Shares', 'Ownership']]

  for (const m of sorted) {
    const entries = [...(entriesMap[m.id] || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    const buyRuleNotes = (notesMap[m.id] || []).filter(n => n.kind === 'buy_rule')

    let shares = entries
      .map(e => {
        const label = e.affiliate ? `${e.call_sign}(${e.affiliate})` : e.call_sign
        return e.deal ? `**${label}-${e.share}%**` : `${label}-${e.share}%`
      })
      .join(' ')
    for (const n of buyRuleNotes) {
      shares += ` (${n.body})`
    }

    // Collect unique ownership values for stations that have a BPL match
    const ownerships = []
    const seen = new Set()
    for (const e of entries) {
      const key = (e.call_sign || '').trim().toUpperCase()
      const ownership = ownershipMap[key]
      if (ownership && !seen.has(ownership)) {
        seen.add(ownership)
        ownerships.push(ownership)
      }
    }

    rows.push([m.state, m.market, shares, ownerships.join(', ')])
  }

  const ws = XLSX.utils.aoa_to_sheet(rows)
  ws['!cols'] = [{ wch: 8 }, { wch: 28 }, { wch: 80 }, { wch: 30 }]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Station Share')
  XLSX.writeFile(wb, 'station_share.xlsx')
}
